import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, X, Phone } from "lucide-react";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/services", label: "Services" },
    { href: "/booking", label: "Book Appointment" },
    { href: "/tax-intake", label: "Tax Intake Form" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass border-b border-white/20 shadow-lg"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_52cd4ddc-ea3b-4da0-bd43-138fc32e1b53/artifacts/hzuq3mo3_EB64C7B1-04AB-4D15-AAC4-830A6D0A1DAC.png" 
              alt="Espady Multi-Service Co. Logo" 
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`font-body text-sm font-medium transition-colors duration-200 hover:text-[#D4AF37] ${
                  isActive(link.href)
                    ? "text-[#0F392B] border-b-2 border-[#D4AF37]"
                    : "text-[#4A5568]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:786-991-8919"
              className="flex items-center gap-2 text-[#0F392B] font-body text-sm font-medium hover:text-[#D4AF37] transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>786-991-8919</span>
            </a>
            <Link to="/booking">
              <Button
                data-testid="book-appointment-btn"
                className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
              >
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-btn"
                className="text-[#0F392B]"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white">
              <div className="flex flex-col gap-8 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`font-body text-lg font-medium transition-colors ${
                      isActive(link.href) ? "text-[#0F392B]" : "text-[#4A5568]"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="pt-4 border-t border-stone-200">
                  <a
                    href="tel:786-991-8919"
                    className="flex items-center gap-2 text-[#0F392B] font-body font-medium mb-4"
                  >
                    <Phone className="w-5 h-5" />
                    <span>786-991-8919</span>
                  </a>
                  <Link to="/booking" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-[#0F392B] text-white rounded-full py-3">
                      Book Appointment
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
